import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import {
  tabletLandscapeQuery,
  ultraWideDesktopQuery,
  wideDesktopQuery,
} from './viewportBreakpoints';

/** Compact hub toolbar labels (Master / Lichess Elite / Auto-show toggles). */
export const hubFilterLabelSx: SystemStyleObject<Theme> = {
  fontSize: 12,
  lineHeight: 1.25,
  [`@media ${tabletLandscapeQuery}`]: {
    fontSize: 14,
  },
  [`@media ${wideDesktopQuery}`]: {
    fontSize: 16,
  },
  [`@media ${ultraWideDesktopQuery}`]: {
    fontSize: 18,
  },
};

/** Allow long hub toggle labels (e.g. Auto-show first game) to wrap to two lines on mobile. */
export const hubAutoShowLabelSx: SystemStyleObject<Theme> = {
  fontSize: 12,
  lineHeight: 1.25,
  whiteSpace: 'normal',
  maxWidth: '5.5rem',
  [`@media ${tabletLandscapeQuery}`]: {
    fontSize: 14,
    maxWidth: '6.5rem',
  },
  [`@media ${wideDesktopQuery}`]: {
    fontSize: 16,
    maxWidth: '7.5rem',
  },
  [`@media ${ultraWideDesktopQuery}`]: {
    fontSize: 18,
    maxWidth: '8.5rem',
  },
};

export const hubFilterFormControlLabelSx: SystemStyleObject<Theme> = {
  m: 0,
  alignItems: 'center',
  '& .MuiFormControlLabel-label': hubFilterLabelSx,
};

/** Prevent body1 typography from overriding compact hub label sizes. */
export const hubFilterFormControlLabelSlotProps = {
  typography: {
    variant: 'inherit' as const,
    sx: hubFilterLabelSx,
  },
};

export const hubFilterToggleWrapperSx: SxProps<Theme> = {
  '& .MuiFormControl-root': { width: 'auto' },
  '& .MuiFormControlLabel-root': {
    alignItems: 'flex-start',
  },
};

export const hubFilterToolbarSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: { xs: 1, sm: 1.5 },
  flexShrink: 0,
  // Bound width so flexWrap can move My games to the next line on narrow screens
  // instead of overflowing and getting clipped by the page shell.
  maxWidth: '100%',
  width: { xs: '100%', sm: 'auto' },
};


export const sectionHeaderSx: SxProps<Theme> = {
  px: 1.25,
  py: { xs: 0.75, sm: 0.85 },
  fontSize: { xs: 12, sm: 13 },
  fontWeight: 600,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'action.hover',
  [`@media ${tabletLandscapeQuery}`]: {
    py: 1,
    fontSize: 14,
  },
  [`@media ${wideDesktopQuery}`]: {
    py: 1.1,
    fontSize: 16,
    px: 1.5,
  },
  [`@media ${ultraWideDesktopQuery}`]: {
    py: 1.25,
    fontSize: 18,
    px: 1.75,
  },
};

export const replayGameRowSx: SxProps<Theme> = {
  cursor: 'pointer',
};

export const seenColumnCellSx: SxProps<Theme> = {
  width: 40,
  maxWidth: 40,
  px: 0.5,
  textAlign: 'center',
  [`@media ${tabletLandscapeQuery}`]: {
    width: 48,
    maxWidth: 48,
    px: 0.75,
  },
  [`@media ${wideDesktopQuery}`]: {
    width: 56,
    maxWidth: 56,
    px: 1,
  },
};

export const gameSummaryPreviewBoardSx: SxProps<Theme> = {
  mt: 1,
  width: '100%',
  pointerEvents: 'none',
  touchAction: 'none',
  userSelect: 'none',
  '& *': {
    pointerEvents: 'none',
    touchAction: 'none',
  },
};

export const compactTableSx: SxProps<Theme> = {
  '& .MuiTableCell-root': {
    fontSize: 12,
    py: 0.35,
    px: 1,
    borderBottom: '1px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap',
    [`@media ${tabletLandscapeQuery}`]: {
      fontSize: 14,
      py: 0.85,
      px: 1.25,
    },
    [`@media ${wideDesktopQuery}`]: {
      fontSize: 16,
      py: 1,
      px: 1.5,
    },
    [`@media ${ultraWideDesktopQuery}`]: {
      fontSize: 18,
      py: 1.1,
      px: 1.75,
    },
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    bgcolor: 'action.hover',
    [`@media ${tabletLandscapeQuery}`]: {
      py: 0.75,
    },
    [`@media ${wideDesktopQuery}`]: {
      py: 0.9,
    },
  },
};

export const wrappedGameTableSx: SxProps<Theme> = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    fontSize: { xs: 12, sm: 13, md: 14 },
    py: { xs: 0.5, sm: 0.65, md: 0.75 },
    px: 1,
    borderBottom: '1px solid',
    borderColor: 'divider',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    [`@media ${wideDesktopQuery}`]: {
      fontSize: 16,
      py: 0.9,
      px: 1.5,
    },
    [`@media ${ultraWideDesktopQuery}`]: {
      fontSize: 18,
      py: 1,
      px: 1.75,
    },
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    bgcolor: 'action.hover',
    py: { xs: 0.35, sm: 0.5, md: 0.6 },
    [`@media ${wideDesktopQuery}`]: {
      py: 0.85,
    },
  },
};

export const wrappedGameRowLine1Sx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr) auto auto',
  columnGap: 1,
  rowGap: 0.25,
  alignItems: 'baseline',
  '& > *': {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
};

export const wrappedGameRowLine2Sx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: 0.5,
  mt: 0.25,
  color: 'text.secondary',
  '& > *': {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
};
